import { useEffect, useMemo, useState } from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Link } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { logEvent } from '../../../utils/eventLog';
import { APP_LINK_STATE } from '../../Home/navRoutes';
import { COLORS } from '../../IndividualPlan/tokens';
import { fetchRosterClassification } from '../../Roster/rosterClassification';
import { CELL_SX, FIRST_CELL_SX, HEAD_ROW_SX, ROW_SX, TABLE_SX } from '../components/GradeTable';
import { DrilldownNotFound, fetchStudentSnapshot, type StudentSnapshot, type StudentSnapshotResponse } from '../drilldownApi';
import { describeListKey, listSelector, loggableListKey, studentsHref } from '../listKeys';
import type { PeReport, ScoreUnit } from '../types';
import { EM_DASH, fmtDelta, fmtLevel, fmtScale } from '../utils/drilldownFormat';
import { DetailCard, DetailStrip, DrilldownFrame, DrilldownPage, NotFoundPage, type LoadedReport } from './DrilldownFrame';
import { ALTERNATE_SCALE, fullName, listContextOf, lookupName, rosterNameOf, rosterNamesOf, type RosterNames } from './StudentListPage';

const DOMAINS: Array<[string, string]> = [
  ['listening', 'Listening'],
  ['speaking', 'Speaking'],
  ['reading', 'Reading'],
  ['writing', 'Writing'],
];

const FROM_ORDER = ['Headline numbers', 'About these counts', 'Section 2.2', 'Grade detail', 'School detail', 'Section 4', 'Cohort detail'];

export function standingLabel(student: Pick<StudentSnapshot, 'standing' | 'cohorts'>): string {
  const cohorts = student.cohorts ?? [];
  if (cohorts.includes('exited')) return 'Exited this year';
  if (cohorts.includes('monitored')) return 'Monitored';
  switch (student.standing) {
    case 'active':
      return 'Active EL';
    case 'reclassified':
    case 'exited':
      return 'Exited this year';
    case 'exit_undated':
      return 'Exited, no date on file';
    case 'monitoring':
      return 'Monitored';
    case 'unreadable':
      return 'Status not readable';
    default:
      return 'Not in the program';
  }
}

export function startedAtLabel(student: StudentSnapshot): string {
  const bucket = student.start_bucket ?? 'none';
  if (student.prior_composite == null && student.prior_composite_scale != null) {
    return `Scale score ${fmtScale(student.prior_composite_scale)} last year, no proficiency level`;
  }
  if (student.prior_composite == null) {
    const domains = DOMAINS.filter(([domain]) => student.prior_domains?.[domain] != null).map(([domain]) => `${domain} ${fmtLevel(student.prior_domains?.[domain])}`);
    if (domains.length > 0) {
      return `No composite level last year: WIDA withholds the composite when a domain is missing. Domains on file: ${domains.join(', ')}.`;
    }
    const scales = DOMAINS.filter(([domain]) => student.prior_domain_scales?.[domain] != null).map(
      ([domain]) => `${domain} ${fmtScale(student.prior_domain_scales?.[domain])}`,
    );
    if (scales.length > 0) {
      return `No composite scale score last year: WIDA withholds the composite when a domain is missing. Domain scale scores on file: ${scales.join(', ')}.`;
    }
    return 'No score last year';
  }
  if (bucket === 'none') return 'No score last year';
  return `Level ${bucket === '4' ? '4 or above' : bucket} · composite ${fmtLevel(student.prior_composite)}`;
}

export function priorAdministration(student: StudentSnapshot): boolean {
  return (
    student.prior_composite != null ||
    student.prior_composite_scale != null ||
    Object.keys(student.prior_domains ?? {}).length > 0 ||
    Object.keys(student.prior_domain_scales ?? {}).length > 0
  );
}

export function tileSubline(
  current: number | null | undefined,
  prior: number | null | undefined,
  resetYear: boolean,
  unit: ScoreUnit = 'proficiency_level',
  alternate = false,
  missingPrior = 'First year tested',
): string {
  if (alternate) return 'Not comparable with a prior ACCESS score';
  const fmt = unit === 'scale_score' ? fmtScale : fmtLevel;
  const delta = (d: number) => (unit === 'scale_score' ? `${d > 0 ? '+' : d < 0 ? '−' : ''}${Math.abs(Math.round(d))}` : fmtDelta(d, ''));
  if (resetYear) return prior != null ? `${fmt(prior)} last spring, on the prior scale` : 'No prior-scale score';
  if (current != null && prior != null) return `${delta(current - prior)} from ${fmt(prior)}`;
  if (current != null && prior == null) return missingPrior;
  return EM_DASH;
}

export function exitCriteria(report: PeReport): Array<{ measure: string; label: string; bar: number }> {
  const config = report.metadata.district_config_snapshot;
  if (config?.CompositeMinimum == null) return [];
  const minimums = Object.fromEntries(Object.entries(config.DomainMinimums ?? {}).map(([k, v]) => [k.toLowerCase(), v]));
  const parts: Array<{ measure: string; label: string; bar: number }> = [{ measure: 'composite', label: 'composite', bar: config.CompositeMinimum }];
  for (const [domain] of DOMAINS) {
    const bar = minimums[domain];
    if (bar != null) parts.push({ measure: domain, label: domain, bar });
  }
  return parts;
}

export function verdictSentence(student: StudentSnapshot, report: PeReport, endYear: number | null): string {
  if (student.composite == null && student.composite_scale != null) {
    return `Scored in scale scores only for Spring ${endYear ?? ''}; the exit criteria are set in proficiency levels.`;
  }
  const domainsOnFile = DOMAINS.map(([domain]) => domain).filter((domain) => student.domains?.[domain] != null);
  if (student.composite == null && domainsOnFile.length > 0) {
    const bars = Object.fromEntries(exitCriteria(report).map(({ measure, bar }) => [measure, bar]));
    const onFile = domainsOnFile
      .map((domain) => `${domain} ${fmtLevel(student.domains?.[domain])}${bars[domain] == null ? '' : ` against the ${bars[domain]} bar`}`)
      .join(', ');
    return `No composite level for Spring ${endYear ?? ''}: WIDA withholds the composite when a domain is missing. Domains on file: ${onFile}.`;
  }
  if (student.composite == null || student.exit_standing === 'no_score') {
    return `No ACCESS score for Spring ${endYear ?? ''}.`;
  }
  const met = student.exit_standing === 'met';
  const criteria = exitCriteria(report).map(({ measure, label, bar }) => {
    const value = measure === 'composite' ? student.composite : student.domains?.[measure];
    return { passed: value != null && value >= bar, text: `${label} ${fmtLevel(value)} against the ${bar} bar` };
  });
  const parts = criteria.map((c) => c.text).join(', ');
  if (met && !criteria.every((c) => c.passed)) {
    return student.exit_year_end != null && student.exit_year_end === endYear
      ? `Reclassified this year on the district’s record; the Spring ${endYear} scores sit below the exit criteria: ${parts}.`
      : `Counted as reclassification-eligible on the district’s record, not on these scores: ${parts}.`;
  }
  const review = !met && student.exit_standing === 'review' ? ' Met the review bar: eligible for the reclassification committee.' : '';
  return `${met ? 'Met' : 'Did not meet'} the district’s exit criteria: ${parts}.${review}`;
}

type SnapshotState =
  | { kind: 'loading' }
  | { kind: 'ready'; response: StudentSnapshotResponse }
  | { kind: 'missing' }
  | { kind: 'error' };

function SnapshotBody({ loaded, studentRef, listKey }: { loaded: LoadedReport; studentRef: string; listKey: string }) {
  const { report, generatedOn, version, schoolYear, endYear, resetYear, unit } = loaded;
  const [state, setState] = useState<SnapshotState>({ kind: 'loading' });
  const [roster, setRoster] = useState<RosterNames>({ kind: 'loading' });
  const ctx = useMemo(() => listContextOf(report), [report]);
  const resolvedKey = state.kind === 'ready' ? (state.response.countedIn.find((key) => listSelector(key) === listKey) ?? listKey) : listKey;
  const listInfo = describeListKey(resolvedKey, ctx);

  useEffect(() => {
    let active = true;
    fetchStudentSnapshot(studentRef, { key: listKey, schoolYear, version })
      .then((response) => {
        if (active) setState({ kind: 'ready', response });
      })
      .catch((err: unknown) => {
        if (active) setState(err instanceof DrilldownNotFound ? { kind: 'missing' } : { kind: 'error' });
      });
    return () => {
      active = false;
    };
  }, [studentRef, listKey, schoolYear, version]);

  useEffect(() => {
    let active = true;
    fetchRosterClassification().then((rows) => {
      if (active) setRoster(rosterNamesOf(rows));
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (state.kind !== 'ready') return;
    void logEvent('program_evaluation.student_viewed.v1', { schoolYear, listKey: loggableListKey(resolvedKey) }).catch(() => undefined);
  }, [state.kind, schoolYear, resolvedKey]);

  const listCrumb = { label: listInfo.title, to: studentsHref(resolvedKey) };

  if (state.kind === 'missing') {
    return <NotFoundPage crumbs={[listCrumb, { label: 'Student' }]} loaded={loaded} message="That student isn’t in this report." />;
  }
  if (state.kind === 'error') {
    return (
      <DrilldownPage crumbs={[listCrumb, { label: 'Student' }]} title="Student">
        <Box role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
          Program Evaluation is temporarily unavailable
        </Box>
      </DrilldownPage>
    );
  }
  if (state.kind === 'loading') {
    return (
      <DrilldownPage crumbs={[listCrumb, { label: 'Student' }]} title="Student">
        <Box aria-label="Loading student" sx={{ fontSize: 14, color: COLORS.muted }}>
          Loading…
        </Box>
      </DrilldownPage>
    );
  }

  const { student, countedIn } = state.response;
  const alternate =
    (student.scale === ALTERNATE_SCALE || student.scale === 'alternate') && (student.composite != null || Object.keys(student.domains ?? {}).length > 0);
  const tileUnit: ScoreUnit = alternate ? 'proficiency_level' : unit;
  const unitNoun = alternate ? 'Alternate ACCESS proficiency level' : unit === 'scale_score' ? 'Scale score' : 'Proficiency level';
  const match = roster.kind === 'ready' ? lookupName(roster.names, [student.student_key, ...(student.alt_keys ?? [])]) : null;
  const name = rosterNameOf(match);
  const title = fullName(match) ?? (match || roster.kind !== 'ready' ? 'Student' : 'Student no longer on the roster');
  const priorTested = priorAdministration(student);
  const missingPrior = (key: string, scale: boolean): string => {
    if (!priorTested) return 'First year tested';
    if (key === 'composite') return scale ? 'No composite scale score last year' : 'No composite level last year';
    return scale ? 'No scale score last year' : 'No level last year';
  };
  const first = name?.first || 'this student';
  const subject = name?.first || 'This student';
  const counted = countedIn
    .map((key) => ({ key, info: describeListKey(key, ctx) }))
    .sort(
      (a, b) =>
        FROM_ORDER.findIndex((o) => a.info.from.startsWith(o)) - FROM_ORDER.findIndex((o) => b.info.from.startsWith(o)),
    );
  const flashlight = student.flashlight;
  const flashlightRows = flashlight
    ? [...DOMAINS.filter(([d]) => flashlight[d]), ...Object.keys(flashlight).filter((d) => !DOMAINS.some(([k]) => k === d)).map((d) => [d, d.charAt(0).toUpperCase() + d.slice(1)] as [string, string])]
    : [];
  const benchmark = (domain: string, window: string) => {
    const value = Object.entries(flashlight?.[domain] ?? {}).find(([name]) => name.toLowerCase() === window.toLowerCase())?.[1];
    return value == null ? EM_DASH : value.toFixed(1);
  };

  return (
    <DrilldownPage crumbs={[listCrumb, { label: title }]} title={title}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1.5,
          borderRadius: '8px',
          bgcolor: COLORS.panel,
          border: `1px solid ${COLORS.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: 14, fontWeight: 500, color: COLORS.ink }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: COLORS.link, mt: '2px', flexShrink: 0 }} />
          <span>{`${subject} as the report counted them on ${generatedOn}. Their record may have changed since.`}</span>
        </Box>
        <Button
          type="button"
          aria-disabled="true"
          onClick={(event) => event.preventDefault()}
          endIcon={<ChevronRightIcon />}
          sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.muted, whiteSpace: 'nowrap', cursor: 'default' }}
        >
          Open current profile
        </Button>
      </Box>

      <DetailStrip
        items={[
          { label: 'Grade', value: student.grade_label || String(student.grade) },
          { label: 'School', value: student.school || 'No school on file' },
          { label: 'Years in EL', value: student.years_in_program != null && student.years_in_program >= 1 ? String(student.years_in_program) : EM_DASH },
          { label: 'Standing', value: standingLabel(student) },
          { label: 'Started the year at', value: startedAtLabel(student) },
        ]}
      />

      <DetailCard
        title={`Spring ${endYear ?? ''} ${alternate ? 'Alternate ACCESS' : 'ACCESS'}`}
        description={
          alternate
            ? 'Alternate ACCESS proficiency level, on its own 1 to 5 scale; not compared with ACCESS or a prior score.'
            : resetYear
              ? `${unitNoun} on the new scale. Last spring’s scores are on the prior scale and are not comparable.`
              : `${unitNoun}, with the change from Spring ${endYear == null ? '' : endYear - 1}.`
        }
        narrative={alternate ? 'Alternate ACCESS results are not measured against the district’s ACCESS exit criteria.' : verdictSentence(student, report, endYear)}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
          {[['composite', 'Composite'] as [string, string], ...DOMAINS].map(([key, label]) => {
            const scale = tileUnit === 'scale_score';
            const current = key === 'composite' ? (scale ? student.composite_scale : student.composite) : (scale ? student.domain_scales : student.domains)?.[key];
            const prior = alternate ? null : key === 'composite' ? (scale ? student.prior_composite_scale : student.prior_composite) : (scale ? student.prior_domain_scales : student.prior_domains)?.[key];
            return (
              <Box
                key={key}
                data-testid="access-tile"
                sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1.5, borderRadius: '8px', bgcolor: COLORS.bg }}
              >
                <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: COLORS.muted }}>
                  {label}
                </Box>
                <Box component="span" sx={{ fontSize: 24, fontWeight: 700, color: COLORS.ink }}>
                  {scale ? fmtScale(current) : fmtLevel(current)}
                </Box>
                <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
                  {tileSubline(current, prior, resetYear, tileUnit, alternate, missingPrior(key, scale))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </DetailCard>

      <DetailCard
        title={`Flashlight benchmarks, ${schoolYear}`}
        description="Rubric 1 to 5."
        foot={flashlight ? undefined : `No Flashlight benchmarks on file for ${first} this year.`}
      >
        {flashlight && (
          <Box data-testid="flashlight-table" sx={TABLE_SX}>
            <Box sx={HEAD_ROW_SX}>
              <Box sx={FIRST_CELL_SX}>Domain</Box>
              <Box sx={{ ...CELL_SX, width: 160 }}>Beginning of year</Box>
              <Box sx={{ ...CELL_SX, width: 160 }}>Middle of year</Box>
              <Box sx={{ ...CELL_SX, width: 160 }}>End of year</Box>
            </Box>
            {flashlightRows.map(([domain, label]) => (
              <Box key={domain} sx={ROW_SX}>
                <Box sx={FIRST_CELL_SX}>{label}</Box>
                <Box sx={{ ...CELL_SX, width: 160 }}>{benchmark(domain, 'BoY')}</Box>
                <Box sx={{ ...CELL_SX, width: 160 }}>{benchmark(domain, 'MoY')}</Box>
                <Box sx={{ ...CELL_SX, width: 160 }}>{benchmark(domain, 'EoY')}</Box>
              </Box>
            ))}
          </Box>
        )}
      </DetailCard>

      <DetailCard
        title={`Where ${first} is counted in this report`}
        description="Every number this student is part of. Open one to go back to that view."
      >
        <Box sx={TABLE_SX}>
          {counted.length === 0 && (
            <Box sx={{ ...ROW_SX, color: COLORS.muted }}>
              <Box sx={FIRST_CELL_SX}>Not in any count this year.</Box>
            </Box>
          )}
          {counted.map(({ key, info }) => (
            <Box key={key} data-testid="counted-row" sx={{ ...ROW_SX, p: 0 }}>
              <Link
                component={RouterLink}
                to={studentsHref(key)}
                state={APP_LINK_STATE}
                underline="none"
                sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, px: 2, py: 1.5, '&:hover': { bgcolor: COLORS.panel } }}
              >
                <Box sx={{ ...CELL_SX, width: 190, color: COLORS.muted, fontSize: 12, fontWeight: 700 }}>
                  {info.from.split(' · ')[0]}
                </Box>
                <Box sx={{ ...FIRST_CELL_SX, color: COLORS.link, fontWeight: 600 }}>{info.title}</Box>
              </Link>
            </Box>
          ))}
        </Box>
      </DetailCard>
    </DrilldownPage>
  );
}

export function StudentSnapshotPage() {
  const { ref = '', key = '' } = useParams<{ ref: string; key: string }>();
  const crumbs = [{ label: describeListKey(key).title, to: studentsHref(key) }, { label: 'Student' }];
  return (
    <DrilldownFrame crumbs={crumbs}>
      {(loaded) => <SnapshotBody key={`${ref}/${key}`} loaded={loaded} studentRef={ref} listKey={key} />}
    </DrilldownFrame>
  );
}
